import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import UploadDialog from "../components/UploadDialog";
import { osmApi } from "../osmApi";
import axios from "../../axios";

const FETCH_LIMIT = 500;
const TOP_BAR_HEIGHT = 64;

const orangeTheme = (outer) =>
  createTheme(outer || {}, {
    palette: {
      primary: {
        main: "#f26a21",
        light: "#ff9a5c",
        dark: "#d4530f",
        contrastText: "#fff",
      },
    },
  });

const CARD_STYLES = {
  total: {
    background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
    color: "#fff",
    label: "rgba(255,255,255,.88)",
    iconBg: "rgba(255,255,255,.2)",
    iconColor: "#fff",
    border: "transparent",
  },
  pending: {
    background: "#fff7ed",
    color: "#9a3412",
    label: "#b45309",
    iconBg: "#ffedd5",
    iconColor: "#ea580c",
    border: "#fed7aa",
  },
  progress: {
    background: "#fffbeb",
    color: "#92400e",
    label: "#b45309",
    iconBg: "#fef3c7",
    iconColor: "#d97706",
    border: "#fde68a",
  },
  checked: {
    background: "#f0fdf4",
    color: "#166534",
    label: "#15803d",
    iconBg: "#dcfce7",
    iconColor: "#16a34a",
    border: "#bbf7d0",
  },
};

const STATUS = {
  pending: { label: "Not started", color: "default" },
  in_progress: { label: "In progress", color: "warning" },
  submitted: { label: "Checked", color: "success" },
};

const idOf = (value) =>
  value && typeof value === "object" ? value.id || value._id : value;

const statusOf = (script) => {
  if (script.status === "submitted") return "submitted";
  if (script.status === "in_progress") return "in_progress";
  return "pending";
};

const parseStorage = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
};

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "?";

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString([], {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "–";

function SummaryCard({ label, value, icon, tone, helper }) {
  const style = CARD_STYLES[tone];

  return (
    <Box
      sx={{
        flex: "1 1 170px",
        minWidth: 0,
        px: 1.5,
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        border: "1px solid",
        borderColor: style.border,
        borderRadius: 2.5,
        background: style.background,
        color: style.color,
        boxShadow: tone === "total" ? "0 6px 16px rgba(234,88,12,.25)" : "none",
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          borderRadius: 2,
          bgcolor: style.iconBg,
          color: style.iconColor,
        }}
      >
        {icon}
      </Box>
      <Box minWidth={0} flex={1}>
        <Typography
          variant="caption"
          fontWeight={600}
          noWrap
          display="block"
          sx={{ color: style.label }}
        >
          {label}
        </Typography>
        <Typography variant="h6" fontWeight={800} lineHeight={1.15} noWrap>
          {value}
        </Typography>
        {helper}
      </Box>
    </Box>
  );
}

function FilterSelect({ label, value, disabled, allLabel, options, onChange }) {
  return (
    <FormControl
      size="small"
      disabled={disabled}
      sx={{ minWidth: 130, flex: "1 1 130px" }}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={(event) => onChange(event.target.value)}
      >
        <MenuItem value="">{allLabel}</MenuItem>
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default function OsmListPage() {
  const navigate = useNavigate();

  const [scripts, setScripts] = useState([]);
  const [students, setStudents] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [statusTab, setStatusTab] = useState("all");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [filterAssessmentId, setFilterAssessmentId] = useState("");
  const [filterStudentId, setFilterStudentId] = useState("");
  const [sort, setSort] = useState({ key: "default", direction: "asc" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const session = useMemo(
    () => ({
      schoolId: parseStorage("schoolId", ""),
      timeTableSessionId: parseStorage("timeTableSessionId", ""),
      teacherId: parseStorage("Teacher_id", ""),
      academicSessionId: parseStorage("academicYearSessionId", ""),
      teacherRoles: parseStorage("role_as_class_teacher", []),
      coordinatorRoles: parseStorage("role_as_co_ordinator", []),
    }),
    [],
  );

  const roles = useMemo(
    () => [
      ...session.teacherRoles.filter((item) => item.is_class_teacher),
      ...session.coordinatorRoles.filter((item) => item.is_class_co_ordinator),
    ],
    [session],
  );

  const classOptions = useMemo(() => {
    const options = new Map();
    roles.forEach((item) => {
      if (item.standard_id) options.set(item.standard_id.id, item.standard_id);
    });
    return [...options.values()].map(({ id, name }) => ({ id, name }));
  }, [roles]);

  const sectionOptions = useMemo(() => {
    const options = new Map();
    roles.forEach((item) => {
      if (item.standard_id?.id === classId && item.section_id) {
        options.set(item.section_id.id, item.section_id);
      }
    });
    return [...options.values()].map(({ id, name }) => ({ id, name }));
  }, [classId, roles]);

  const studentsById = useMemo(
    () =>
      new Map(students.map((student) => [student.id || student._id, student])),
    [students],
  );

  const assessmentsById = useMemo(
    () =>
      new Map(
        assessments.map((assessment) => [
          assessment.id || assessment._id,
          assessment,
        ]),
      ),
    [assessments],
  );

  const fetchScripts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await osmApi.listScripts({ page: 1, limit: FETCH_LIMIT });
      setScripts(res.data || []);
    } catch (fetchError) {
      console.error(fetchError);
      setError("Could not load answer books. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScripts();

    axios
      .get(`/api/v1/school/student/get/school/${session.schoolId}`)
      .then((res) => setStudents(res.data.data || []))
      .catch((fetchError) => console.error("Student Error:", fetchError));

    axios
      .get(
        `/api/v1/assessment/filterByTeacherId/${session.teacherId}/review?sortBy=-1&_page=1&_limit=100&academicSessionId=${session.academicSessionId}&timetableSessionId=${session.timeTableSessionId}`,
      )
      .then((res) => setAssessments(res.data.data || []))
      .catch((fetchError) => console.error("Assessment Error:", fetchError));
  }, [fetchScripts, session]);

  const rows = useMemo(
    () =>
      scripts.map((script) => {
        const rawStudent = script.studentId;
        const rawAssessment = script.assessmentId;
        const studentId = idOf(rawStudent);
        const assessmentId = idOf(rawAssessment);
        const student =
          studentsById.get(studentId) ||
          (typeof rawStudent === "object" ? rawStudent : null);
        const assessment =
          assessmentsById.get(assessmentId) ||
          (typeof rawAssessment === "object" ? rawAssessment : null);

        return {
          id: script._id || script.id,
          studentId,
          assessmentId,
          studentName: student?.name || "Unknown student",
          admissionNumber: student?.admissionNumber || "",
          classId: student?.standard?.id,
          sectionId: student?.section?.id,
          classLabel: [student?.standard?.name, student?.section?.name]
            .filter(Boolean)
            .join(" - "),
          examName:
            script.paperScheme?.name || assessment?.name || "Assessment",
          subject: assessment?.subject_id?.name || "",
          fileName: script.name || "Untitled.pdf",
          awarded:
            script.totalAwarded ??
            script.awardedMarks ??
            script.marksAwarded ??
            script.total ??
            null,
          maxMarks: script.paperScheme?.totalMarks ?? null,
          status: statusOf(script),
          updatedAt: script.updatedAt,
        };
      }),
    [assessmentsById, scripts, studentsById],
  );

  const assessmentOptions = useMemo(
    () =>
      assessments
        .filter(
          (assessment) =>
            (!classId || assessment.standard_id?.id === classId) &&
            (!sectionId || assessment.section_id?.id === sectionId),
        )
        .map((assessment) => ({ id: assessment.id, name: assessment.name })),
    [assessments, classId, sectionId],
  );

  const studentOptions = useMemo(
    () =>
      students
        .filter(
          (student) =>
            student.standard?.id === classId &&
            student.section?.id === sectionId &&
            student.timetableSession?.id === session.timeTableSessionId,
        )
        .map((student) => ({
          id: student.id,
          name: `${student.name} (Adm: ${student.admissionNumber})`,
        })),
    [classId, sectionId, session.timeTableSessionId, students],
  );

  // Status tabs apply on top of these filters, so the tab counts stay useful.
  const scopedRows = useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      if (classId && row.classId !== classId) return false;
      if (sectionId && row.sectionId !== sectionId) return false;
      if (filterAssessmentId && row.assessmentId !== filterAssessmentId)
        return false;
      if (filterStudentId && row.studentId !== filterStudentId) return false;
      if (!term) return true;

      return [
        row.studentName,
        row.admissionNumber,
        row.examName,
        row.subject,
        row.fileName,
      ]
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [classId, filterAssessmentId, filterStudentId, rows, search, sectionId]);

  const counts = useMemo(() => {
    const result = {
      all: scopedRows.length,
      pending: 0,
      in_progress: 0,
      submitted: 0,
    };
    scopedRows.forEach((row) => {
      result[row.status] += 1;
    });
    return result;
  }, [scopedRows]);

  const visibleRows = useMemo(() => {
    const filtered =
      statusTab === "all"
        ? scopedRows
        : scopedRows.filter((row) => row.status === statusTab);

    const direction = sort.direction === "asc" ? 1 : -1;
    const rank = { in_progress: 0, pending: 1, submitted: 2 };

    return [...filtered].sort((a, b) => {
      if (sort.key === "student")
        return a.studentName.localeCompare(b.studentName) * direction;
      if (sort.key === "exam")
        return a.examName.localeCompare(b.examName) * direction;
      if (sort.key === "updated") {
        return (
          (new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0)) * direction
        );
      }

      // Default: unfinished work first, then the most recently touched.
      return (
        rank[a.status] - rank[b.status] ||
        new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
      );
    });
  }, [scopedRows, sort, statusTab]);

  const pagedRows = visibleRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );
  const nextScript =
    scopedRows.find((row) => row.status === "in_progress") ||
    scopedRows.find((row) => row.status === "pending");
  const progress = counts.all
    ? Math.round((counts.submitted / counts.all) * 100)
    : 0;
  const hasFilters = Boolean(
    search || classId || sectionId || filterAssessmentId || filterStudentId,
  );

  useEffect(() => {
    setPage(0);
  }, [
    classId,
    filterAssessmentId,
    filterStudentId,
    rowsPerPage,
    search,
    sectionId,
    sort,
    statusTab,
  ]);

  const clearFilters = () => {
    setSearch("");
    setClassId("");
    setSectionId("");
    setFilterAssessmentId("");
    setFilterStudentId("");
    setStatusTab("all");
  };

  const toggleSort = (key) => {
    setSort((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleUploadSuccess = (response) => {
    setIsUploadOpen(false);
    const newId = response?.data?._id || response?.data?.id;

    if (newId) {
      navigate(`/osm/${newId}`);
      return;
    }

    setNotice("Upload finished. Refreshing the list.");
    fetchScripts();
  };

  const sortableHead = (key, label) => (
    <TableSortLabel
      active={sort.key === key}
      direction={sort.key === key ? sort.direction : "asc"}
      onClick={() => toggleSort(key)}
    >
      {label}
    </TableSortLabel>
  );

  const totalPages = Math.max(1, Math.ceil(visibleRows.length / rowsPerPage));
  const firstRow = visibleRows.length ? page * rowsPerPage + 1 : 0;
  const lastRow = Math.min((page + 1) * rowsPerPage, visibleRows.length);

  return (
    <ThemeProvider theme={orangeTheme}>
      <Box
        sx={{
          width: "100%",
          maxWidth: 1400,
          minWidth: 0,
          mx: "auto",

          boxSizing: "border-box",
          height: `calc(100vh - ${TOP_BAR_HEIGHT}px)`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          flexWrap="wrap"
          gap={1}
          justifyContent="space-between"
          alignItems="center"
          mb={1.5}
          flexShrink={0}
        >
          <Box>
            <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
              Online Script Marking
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Upload, check and track student answer books
            </Typography>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={1}>
            {/* {nextScript && (
              <Button
                size="small"
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate(`/osm/${nextScript.id}`)}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Continue marking
              </Button>
            )} */}
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={<CloudUploadIcon />}
              onClick={() => setIsUploadOpen(true)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Upload answer book
            </Button>
          </Box>
        </Box>

        {/* Summary */}
        <Box display="flex" flexWrap="wrap" gap={1} mb={1.5} flexShrink={0}>
          <SummaryCard
            tone="total"
            label="Total answer books"
            value={counts.all}
            icon={<AssignmentOutlinedIcon fontSize="small" />}
          />
          <SummaryCard
            tone="pending"
            label="Not started"
            value={counts.pending}
            icon={<HourglassEmptyOutlinedIcon fontSize="small" />}
          />
          <SummaryCard
            tone="progress"
            label="In progress"
            value={counts.in_progress}
            icon={<EditNoteOutlinedIcon fontSize="small" />}
          />
          <SummaryCard
            tone="checked"
            label="Checked"
            value={`${counts.submitted} of ${counts.all}`}
            icon={<TaskAltOutlinedIcon fontSize="small" />}
            helper={
              <LinearProgress
                variant="determinate"
                value={progress}
                color="success"
                sx={{ mt: 0.5, height: 4, borderRadius: 2, bgcolor: "#dcfce7" }}
              />
            }
          />
        </Box>

        {/* Filters */}
        <Paper
          variant="outlined"
          sx={{ p: 1, mb: 1.5, borderRadius: 2, flexShrink: 0 }}
        >
          <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
            <TextField
              size="small"
              value={search}
              placeholder="Search student, admission no. or exam"
              inputProps={{ "aria-label": "Search answer books" }}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ flex: "2 1 240px" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FilterSelect
              label="Class"
              value={classId}
              allLabel="All classes"
              options={classOptions}
              onChange={(value) => {
                setClassId(value);
                setSectionId("");
                setFilterAssessmentId("");
                setFilterStudentId("");
              }}
            />
            <FilterSelect
              label="Section"
              value={sectionId}
              disabled={!classId}
              allLabel="All sections"
              options={sectionOptions}
              onChange={(value) => {
                setSectionId(value);
                setFilterAssessmentId("");
                setFilterStudentId("");
              }}
            />
            <FilterSelect
              label="Assessment"
              value={filterAssessmentId}
              allLabel="All assessments"
              options={assessmentOptions}
              onChange={setFilterAssessmentId}
            />
            <FilterSelect
              label="Student"
              value={filterStudentId}
              disabled={!sectionId}
              allLabel="All students"
              options={studentOptions}
              onChange={setFilterStudentId}
            />
            {hasFilters && (
              <Button
                size="small"
                color="inherit"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                sx={{ textTransform: "none" }}
              >
                Clear
              </Button>
            )}
            <Tooltip arrow title="Refresh list">
              <span>
                <IconButton
                  size="small"
                  aria-label="Refresh list"
                  disabled={loading}
                  onClick={fetchScripts}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Paper>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 1.5, flexShrink: 0 }}
            action={
              <Button color="inherit" size="small" onClick={fetchScripts}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Table */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Tabs
            value={statusTab}
            variant="scrollable"
            onChange={(_, value) => setStatusTab(value)}
            sx={{
              minHeight: 38,
              flexShrink: 0,
              px: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              "& .MuiTab-root": {
                minHeight: 38,
                py: 0.5,
                textTransform: "none",
              },
            }}
          >
            <Tab value="all" label={`All (${counts.all})`} />
            <Tab value="pending" label={`Not started (${counts.pending})`} />
            <Tab
              value="in_progress"
              label={`In progress (${counts.in_progress})`}
            />
            <Tab value="submitted" label={`Checked (${counts.submitted})`} />
          </Tabs>

          <TableContainer sx={{ flex: 1, maxHeight: 300, overflow: "auto" }}>
            <Table stickyHeader size="small" sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: 700,
                      bgcolor: "#fff7ed",
                      color: "#9a3412",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>{sortableHead("student", "Student")}</TableCell>
                  <TableCell>{sortableHead("exam", "Assessment")}</TableCell>
                  <TableCell>Marks</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>
                    {sortableHead("updated", "Last updated")}
                  </TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading &&
                  Array.from({ length: 6 }, (_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={6}>
                        <Skeleton height={32} />
                      </TableCell>
                    </TableRow>
                  ))}

                {!loading && pagedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <DescriptionIcon
                        sx={{ fontSize: 40, color: "#fdba74", mb: 1 }}
                      />
                      <Typography fontWeight={700}>
                        {scripts.length
                          ? "No answer books match these filters"
                          : "No answer books yet"}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.5 }}
                      >
                        {scripts.length
                          ? "Try a different search or clear the filters."
                          : "Upload the first answer book to start marking."}
                      </Typography>
                      {scripts.length ? (
                        <Button
                          onClick={clearFilters}
                          sx={{ textTransform: "none" }}
                        >
                          Clear filters
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          disableElevation
                          onClick={() => setIsUploadOpen(true)}
                          sx={{ textTransform: "none" }}
                        >
                          Upload answer book
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )}

                {!loading &&
                  pagedRows.map((row) => {
                    const status = STATUS[row.status];
                    const checked = row.status === "submitted";
                    const details = [
                      row.admissionNumber && `Adm: ${row.admissionNumber}`,
                      row.classLabel,
                    ]
                      .filter(Boolean)
                      .join(" · ");

                    return (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => navigate(`/osm/${row.id}`)}
                        sx={{
                          cursor: "pointer",
                          "& td": { py: 0.6 },
                          "&.MuiTableRow-hover:hover": { bgcolor: "#fff7ed" },
                        }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.25}>
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                fontSize: 12,
                                fontWeight: 700,
                                bgcolor: "#ffedd5",
                                color: "#c2410c",
                              }}
                            >
                              {initials(row.studentName)}
                            </Avatar>
                            <Box minWidth={0}>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                noWrap
                              >
                                {row.studentName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                display="block"
                              >
                                {details || row.fileName}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Tooltip arrow title={row.fileName}>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {row.examName}
                              </Typography>
                              {row.subject && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {row.subject}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            component="span"
                          >
                            {row.awarded ?? "–"}
                          </Typography>
                          {row.maxMarks !== null && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              component="span"
                            >
                              {" "}
                              / {row.maxMarks}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={status.label}
                            color={status.color}
                            variant="outlined"
                          />
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(row.updatedAt)}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Button
                            size="small"
                            disableElevation
                            variant={checked ? "outlined" : "contained"}
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(`/osm/${row.id}`);
                            }}
                            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                          >
                            {checked
                              ? "View"
                              : row.status === "in_progress"
                                ? "Continue"
                                : "Start marking"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              flexShrink: 0,
              minHeight: 46,
              px: 1.5,
              py: 0.75,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              borderTop: "1px solid #ffedd5",
              bgcolor: "#fffaf5",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Showing{" "}
              <strong>
                {firstRow}–{lastRow}
              </strong>{" "}
              of <strong>{visibleRows.length}</strong>
            </Typography>

            <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5}>
              <Box display="flex" alignItems="center" gap={0.75}>
                <Typography variant="caption" color="text.secondary">
                  Rows per page
                </Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  inputProps={{ "aria-label": "Rows per page" }}
                  onChange={(event) =>
                    setRowsPerPage(Number(event.target.value))
                  }
                  sx={{
                    height: 30,
                    fontSize: 13,
                    bgcolor: "#fff",
                    "& .MuiSelect-select": { py: 0.25 },
                  }}
                >
                  {[10, 25, 50, 100].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Pagination
                size="small"
                color="primary"
                shape="rounded"
                count={totalPages}
                page={page + 1}
                siblingCount={0}
                boundaryCount={1}
                onChange={(_, value) => setPage(value - 1)}
              />
            </Box>
          </Box>
        </Paper>

        <UploadDialog
          open={isUploadOpen}
          onClose={() => setIsUploadOpen(false)}
          onSuccess={handleUploadSuccess}
        />

        <Snackbar
          open={Boolean(notice)}
          autoHideDuration={3500}
          message={notice}
          onClose={() => setNotice("")}
        />
      </Box>
    </ThemeProvider>
  );
}
